import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import AgreementModal from "../components/AgreementModal";
import Cookies from "js-cookie";
import axios from "axios";
import API_ENDPOINTS from "../config/api";

// 쿠키 설정 가져오기
const { KEYS: COOKIE_KEYS } = API_ENDPOINTS.COOKIE_CONFIG;

// 페이지 애니메이션 변형 설정
const pageVariants = {
  initial: {
    opacity: 0,
    y: 50,
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      type: "tween",
      ease: "easeInOut",
      duration: 0.6, // 애니메이션 시간 단축
    },
  },
  exit: {
    opacity: 0,
    y: -50,
    transition: {
      duration: 0.4, // 애니메이션 시간 단축
    },
  },
};

function BondAgreementPage() {
  const navigate = useNavigate();
  const pageRef = useRef(null);
  const confirmButtonRef = useRef(null);
  const topRef = useRef(null);
  const [userInfo, setUserInfo] = useState(null);
  const [isGeneratingDelegation, setIsGeneratingDelegation] = useState(false);

  const [agreements, setAgreements] = useState({
    agreement1: false,
    agreement2: false,
    agreement3: false,
    agreement4: false,
    agreement5: false,
  });

  const [isButtonEnabled, setIsButtonEnabled] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentAgreementId, setCurrentAgreementId] = useState(null);
  const [modalContent, setModalContent] = useState({
    title: "",
    content: null,
  });

  // 쿠키에서 사용자 정보 가져오기
  useEffect(() => {
    const userInfoFromCookie = Cookies.get(COOKIE_KEYS.USER_INFO);
    if (userInfoFromCookie) {
      try {
        const parsedUserInfo = JSON.parse(userInfoFromCookie);
        setUserInfo(parsedUserInfo);
      } catch (error) {}
    }
  }, []);

  // 모든 약관에 동의했는지 확인하여 버튼 활성화 상태 업데이트
  useEffect(() => {
    const allAgreed = Object.values(agreements).every(
      (value) => value === true,
    );
    setIsButtonEnabled(allAgreed);

    // 모든 약관에 동의했을 때 하단으로 스크롤
    if (allAgreed) {
      setTimeout(() => {
        scrollToConfirmButton();
      }, 100);
    }
  }, [agreements]);

  // 페이지를 벗어날 때 스크롤 초기화
  useEffect(() => {
    return () => {
      // 컴포넌트 언마운트 시 스크롤을 최상단으로 초기화
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };
  }, []);

  // 모달 내용 정의
  const agreementContents = {
    agreement1: {
      title: "하자보수 및 손해배상청구권 양도",
      content: (
        <div>
          <p>
            1. "갑"은 하자보수의무자(시행사, 시공사, 보증사 등 사업주체)에
            대하여 가지고 있는 하자보수청구 및 하자보수에 갈음하는
            손해배상금(이자 및 지연손해금 포함)청구권을 "을"에게 양도하기로 하고
            이에 본 양도증서에 서명날인한다.
          </p>
        </div>
      ),
    },
    agreement2: {
      title: "채권 행사 및 소송 권한 위임",
      content: (
        <div>
          <p>
            2. "갑"은 위 하자보수의무자들을 상대로 한 위 손해배상채권의 양도통지
            및 손해배상채권행사 등 하자소송에 관한 일체의 권한(단, 준공전 하자를
            포함한 합의종결권한은 제외함. 만일, 준공전 하자를 포함하여
            합의종결을 할 경우, "을"은 개별 구분소유자로부터 다시 동의를 받아야
            하고 전체 85% 이상의 동의요건을 충족하여야만 합의종결이 가능함)을
            "을"에게 위임한다.
          </p>
        </div>
      ),
    },
    agreement3: {
      title: "판결원리금(합의금, 가지급금 포함)",
      content: (
        <div>
          <p>
            3. 판결원리금(합의금, 가지급금 포함)의 보관・관리・사용・분배 등
            처분방법은 “을”의 의결에 따르되, ㉮ 계약에 의한 변호사보수 및
            대납소송비용은 위 승소금에서 우선공제・정산하고, ㉯ “㉮”항의 비용을
            제한 승소금 중 전유부분은 각 세대(구분소유자)에 지급하고,
            공용부분(안전관리비, 소송비용액확정결정에 따른 회수금, 가지급금 등
            판결원리금에 대한 은행예치이자 포함)은 “을”의 의결에 따라 하자보수를
            위한 공사비용으로 사용함에 “갑(승계인 포함)”은 동의한다.
          </p>
        </div>
      ),
    },
    agreement4: {
      title: "동의 및 철회 불가 확인",
      content: (
        <div>
          <p>
            4. "갑"은 본 동의(계약)가 정상적인 의사와 권한으로
            성립(체결)되었음을 확인하고, 동의의사를 철회하지 않기로 하며, "을"이
            "갑"을 위하여 하자보수청구 및 하자담보추급권 행사(하자소송 포함)등
            하자관련 업무를 진행함에 있어 개인정보수집·이용·제공에 동의한다.
          </p>
        </div>
      ),
    },
    agreement5: {
      title: "개인정보 처리방침",
      content: (
        <div>
          <h3
            style={{
              fontSize: "16px",
              fontWeight: "600",
              marginBottom: "16px",
              color: "#1F2937",
            }}
          >
            개인정보 처리방침
          </h3>
          <div
            style={{ fontSize: "14px", lineHeight: "1.6", color: "#374151" }}
          >
            <p style={{ marginBottom: "12px" }}>
              본 서비스는 하자보수 관련 업무 진행을 위해 다음과 같이 개인정보를
              수집·이용합니다.
            </p>

            <h4
              style={{
                fontSize: "15px",
                fontWeight: "600",
                marginBottom: "8px",
                color: "#1F2937",
              }}
            >
              1. 수집하는 개인정보 항목
            </h4>
            <p style={{ marginBottom: "12px" }}>
              • 필수항목: 성명, 생년월일, 연락처, 주소(동/호수)
              <br />• 선택항목: 공동소유자 정보(성명, 생년월일, 연락처)
            </p>

            <h4
              style={{
                fontSize: "15px",
                fontWeight: "600",
                marginBottom: "8px",
                color: "#1F2937",
              }}
            >
              2. 개인정보의 수집 및 이용목적
            </h4>
            <p style={{ marginBottom: "12px" }}>
              • 하자보수청구 및 손해배상청구권 행사
              <br />
              • 소송 진행 및 법적 절차 수행
              <br />• 승소금 분배 및 정산 업무
            </p>

            <h4
              style={{
                fontSize: "15px",
                fontWeight: "600",
                marginBottom: "8px",
                color: "#1F2937",
              }}
            >
              3. 개인정보의 보유 및 이용기간
            </h4>
            <p style={{ marginBottom: "12px" }}>
              • 목적 달성 후 즉시 파기 (단, 관련 법령에 따라 보존이 필요한 경우
              해당 기간까지 보관)
            </p>

            <h4
              style={{
                fontSize: "15px",
                fontWeight: "600",
                marginBottom: "8px",
                color: "#1F2937",
              }}
            >
              4. 개인정보의 제3자 제공
            </h4>
            <p style={{ marginBottom: "12px" }}>
              • 법원, 변호사, 하자보수의무자 등 업무 수행에 필요한 기관에만 제공
              <br />• 제공 시 사전 동의를 받으며, 목적 외 사용을 금지합니다
            </p>

            <p
              style={{ marginTop: "16px", fontSize: "13px", color: "#6B7280" }}
            >
              ※ 위 개인정보 처리방침에 동의하지 않으시는 경우 서비스 이용이
              제한될 수 있습니다.
            </p>
          </div>
        </div>
      ),
    },
  };

  // 부드러운 스크롤 함수
  const scrollToConfirmButton = () => {
    if (confirmButtonRef.current) {
      confirmButtonRef.current.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    }
  };

  // 위임장 생성 함수
  // const handleGenerateDelegation = async () => {
  //   if (!isButtonEnabled) {
  //     return;
  //   }

  //   setIsGeneratingDelegation(true);

  //   try {
  //     // buildingUnitInfo 쿠키에서 모든 데이터 가져오기
  //     const buildingUnitCookie = Cookies.get("buildingUnitInfo");

  //     if (!buildingUnitCookie) {
  //       throw new Error("건물 정보를 찾을 수 없습니다.");
  //     }

  //     const buildingUnitInfo = JSON.parse(buildingUnitCookie);

  //     // API 요청 데이터 준비 - API가 원하는 형식에 맞게
  //     const requestData = {
  //       data: [
  //         {
  //           동: buildingUnitInfo.building?.trim() || "",
  //           호수: buildingUnitInfo.unit?.trim() || "",
  //           이름: buildingUnitInfo.applicantName?.trim() || "",
  //           생년월일: buildingUnitInfo.applicantBirthDate?.trim() || "",
  //         },
  //       ],
  //     };

  //     // 공동소유자 정보가 있으면 이름2, 생년월일2 등의 형태로 추가
  //     if (buildingUnitInfo.coOwners && buildingUnitInfo.coOwners.length > 0) {
  //       buildingUnitInfo.coOwners.forEach((coOwner, index) => {
  //         const suffix = index + 2; // 2부터 시작 (이름2, 생년월일2...)
  //         requestData.data[0][`이름${suffix}`] = coOwner.name?.trim() || "";
  //         requestData.data[0][`생년월일${suffix}`] =
  //           coOwner.birthDate?.trim() || "";
  //       });
  //     }

  //     // API 호출
  //     const response = await fetch(
  //       "https://aid100.co.kr/api/generate-delegations-hwp2",
  //       {
  //         method: "POST",
  //         headers: {
  //           "Content-Type": "application/json",
  //         },
  //         body: JSON.stringify(requestData),
  //       }
  //     );

  //     if (!response.ok) {
  //       const errorData = await response.json();
  //       throw new Error(errorData.error || "위임장 생성에 실패했습니다.");
  //     }

  //     // ZIP 파일 다운로드
  //     const blob = await response.blob();
  //     const url = window.URL.createObjectURL(blob);
  //     const link = document.createElement("a");
  //     link.href = url;
  //     link.download = "delegations_hwp2.zip";
  //     document.body.appendChild(link);
  //     link.click();
  //     document.body.removeChild(link);
  //     window.URL.revokeObjectURL(url);

  //     alert("위임장이 성공적으로 생성되었습니다!");

  //     // 위임장 생성 완료 후 signature-agreement 페이지로 이동
  //     setTimeout(() => {
  //       navigate("/signature-agreement");
  //     }, 1000); // 1초 후 이동
  //   } catch (error) {
  //     console.error("위임장 생성 오류:", error);
  //     alert(`위임장 생성에 실패했습니다: ${error.message}`);
  //   } finally {
  //     setIsGeneratingDelegation(false);
  //   }
  // };

  // 다음 버튼 클릭 처리 - 동의 API 호출 후 이동
  const handleNextButtonClick = async () => {
    if (!isButtonEnabled) return;

    try {
      // PUT /claim/agree API 호출

      const response = await axios.put(
        `${API_ENDPOINTS.BASE_URL}/mobile/claim/agree`,
      );

      // 스크롤을 최상단으로 초기화
      if (topRef.current) {
        topRef.current.scrollIntoView({
          behavior: "auto",
          block: "start",
        });
      }

      // 약간의 지연 후 다음 페이지로 이동
      setTimeout(() => {
        navigate("/signature-agreement");
      }, 100);

      const buildingUnitInfo = Cookies.get("buildingUnitInfo");
      // if (buildingUnitInfo) {
      //   try {
      //     const parsedBuildingUnitInfo = JSON.parse(buildingUnitInfo);

      //     // API 요청 데이터 구성
      //     const requestData = {
      //       data: [
      //         {
      //           동: parsedBuildingUnitInfo.building || "",
      //           호수: parsedBuildingUnitInfo.unit || "",
      //           이름: parsedBuildingUnitInfo.applicantName || "",
      //           생년월일: parsedBuildingUnitInfo.applicantBirthDate || "",
      //         },
      //       ],
      //     };

      //     // apartmentCode가 쿠키에 있으면 추가
      //     const apartmentCode = Cookies.get("apartmentCode");
      //     if (apartmentCode) {
      //       requestData.apartmentCode = apartmentCode;
      //     }

      //     // 공동소유자 정보 추가
      //     if (
      //       parsedBuildingUnitInfo.coOwners &&
      //       parsedBuildingUnitInfo.coOwners.length > 0
      //     ) {
      //       parsedBuildingUnitInfo.coOwners.forEach((coOwner, index) => {
      //         const coOwnerNumber = index + 2; // 2부터 시작
      //         requestData.data[0][`이름${coOwnerNumber}`] = coOwner.name || "";
      //         requestData.data[0][`생년월일${coOwnerNumber}`] =
      //           coOwner.birthDate || "";
      //       });
      //     }

      //     // 위임장 생성 API 호출
      //     await axios.post(
      //       "https://aid100.co.kr/api/generate-delegations-hwp2",
      //       requestData,
      //       {
      //         headers: {
      //           "Content-Type": "application/json",
      //         },
      //       }
      //     );
      //   } catch (delegationError) {
      //     console.error("위임장 생성 API 호출 실패:", delegationError);

      //   }
      // }

      // 동의 API 호출 성공 후 /id-card-submit으로 이동
    } catch (error) {
      console.error("다음 버튼 - 동의 API 호출 실패:", error);
      alert("문제가 발생했습니다. 다시 시도해주세요.");
      navigate("/");
    }
  };

  // 동의 항목 클릭 처리 - 어떤 영역을 클릭하더라도 모달이 열림
  const handleAgreementItemClick = (e, id) => {
    // 모달 열기
    if (agreementContents[id]) {
      setCurrentAgreementId(id);
      setModalContent({
        title: agreementContents[id].title,
        content: agreementContents[id].content,
      });
      setModalOpen(true);
    }
  };

  // 모달에서 동의 버튼 클릭 처리
  const handleAgree = (agreementId) => {
    setAgreements((prev) => ({
      ...prev,
      [agreementId]: true,
    }));

    // 모달 닫기
    setModalOpen(false);
  };

  return (
    <motion.div
      className="page-container"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      ref={pageRef}
    >
      {/* 최상단 ref 요소 */}
      <div
        ref={topRef}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "1px",
          height: "1px",
        }}
      />
      <motion.div
        className="page-header"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.1 }}
        style={{
          marginTop: "10px",
        }}
      >
        <h1>채권양도·양수 동의</h1>
      </motion.div>

      <motion.div
        className="logo-container"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, delay: 0.1 }}
      >
        <img src="/mainLogo.png" alt="메인 로고" className="main-logo" />
      </motion.div>

      <motion.div
        className="agreement-content"
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
      >
        <h2>계약 내용에 동의해주세요</h2>
        <p>채권 양도 양수에 필요한 사항을 확인해 주세요.</p>

        <div className="agreement-items">
          {Object.keys(agreementContents).map((id, index) => (
            <motion.div
              key={id}
              className="agreement-item"
              onClick={(e) => handleAgreementItemClick(e, id)}
              initial={{ opacity: 0, x: -15 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4, delay: 0.2 + index * 0.05 }}
              whileHover={{ scale: 1.01 }}
              style={
                id === "agreement5"
                  ? {
                      backgroundColor: "#fafaff",
                      border: "1px solid #7A4FFF",
                    }
                  : {}
              }
            >
              <div className="checkbox-image">
                <img
                  src={agreements[id] ? "/checked.png" : "/checkedInActive.png"}
                  alt="체크박스"
                />
              </div>
              <label>
                {agreementContents[id].title}
                <span className="required">[필수]</span>
              </label>
              <div className="arrow-icon">›</div>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* <motion.button
        className="confirm-button"
        disabled={!isButtonEnabled || isGeneratingDelegation}
        onClick={handleGenerateDelegation}
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.4 }}
        whileHover={
          isButtonEnabled && !isGeneratingDelegation ? { scale: 1.02 } : {}
        }
        whileTap={
          isButtonEnabled && !isGeneratingDelegation ? { scale: 0.98 } : {}
        }
        ref={confirmButtonRef}
      >
        {isGeneratingDelegation ? "위임장 생성 중..." : "위임장 생성(테스트)"}
      </motion.button> */}

      <motion.button
        className="confirm-button"
        disabled={!isButtonEnabled}
        onClick={handleNextButtonClick}
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.5 }}
        whileHover={isButtonEnabled ? { scale: 1.02 } : {}}
        whileTap={isButtonEnabled ? { scale: 0.98 } : {}}
        ref={confirmButtonRef}
        style={{
          width: "95%",
          maxWidth: "460px",
          height: "56px",
          fontSize: "16px",
          fontWeight: "600",
          background: isButtonEnabled ? "#7A4FFF" : "#EBEBFF",
          color: isButtonEnabled ? "#EBEBFF" : "#fff",
          border: "none",
          borderRadius: "8px",
          cursor: isButtonEnabled ? "pointer" : "not-allowed",
          transition: "background-color 0.2s ease",
          boxShadow: isButtonEnabled
            ? "0 2px 5px rgba(0, 117, 255, 0.2)"
            : "none",
          marginBottom: "30px",
        }}
      >
        다음
      </motion.button>

      <AgreementModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={modalContent.title}
        content={modalContent.content}
        agreementId={currentAgreementId}
        onAgree={handleAgree}
        userInfo={userInfo}
      />

      {/* 로딩 모달 */}
      {isGeneratingDelegation && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "12px",
              padding: "24px",
              width: "80%",
              maxWidth: "320px",
              textAlign: "center",
            }}
          >
            <div
              style={{
                width: "50px",
                height: "50px",
                borderRadius: "25px",
                borderTop: "3px solid #7A4FFF",
                borderRight: "3px solid transparent",
                margin: "0 auto 16px",
                animation: "spin 1s linear infinite",
              }}
            ></div>
            <h3
              style={{
                margin: "0 0 8px 0",
                fontSize: "18px",
                fontWeight: "600",
                color: "#1F2937",
              }}
            >
              위임장 생성 중
            </h3>
            <p
              style={{
                margin: 0,
                fontSize: "14px",
                color: "#6B7280",
              }}
            >
              잠시만 기다려주세요...
            </p>
          </div>
        </div>
      )}

      {/* 스피너 애니메이션 CSS */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </motion.div>
  );
}

export default BondAgreementPage;
